import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const WellnessImageGallery = ({ images = [], title = '', virtualTour = '', video = '' }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [showFullscreen, setShowFullscreen] = useState(false);

  const handlePrevious = () => {
    setSelectedImage(prev => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setSelectedImage(prev => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleThumbnailClick = (index) => {
    setSelectedImage(index);
  };

  const handleFullscreen = () => {
    setShowFullscreen(true);
  };

  return (
    <div className="bg-surface rounded-lg overflow-hidden shadow-elevation-1">
      {/* Main Image Container */}
      <div className="relative h-64 sm:h-80 lg:h-96">
        <Image
          src={images[selectedImage]}
          alt={`${title} - Image ${selectedImage + 1}`}
          className="w-full h-full object-cover"
        />
        
        {/* Navigation Arrows */}
        {images?.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-surface/90 backdrop-blur-sm text-text-primary p-3 rounded-full shadow-elevation-2 hover:bg-surface transition-all duration-200"
              aria-label="Previous image"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-surface/90 backdrop-blur-sm text-text-primary p-3 rounded-full shadow-elevation-2 hover:bg-surface transition-all duration-200"
              aria-label="Next image"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          </>
        )}

        {/* Action Buttons */}
        <div className="absolute top-4 right-4 flex space-x-2">
          {virtualTour && (
            <button
              onClick={() => window.open(virtualTour, '_blank')}
              className="flex items-center space-x-2 bg-accent text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-accent-600 transition-all duration-200 shadow-elevation-2"
            >
              <Icon name="Eye" size={16} />
              <span>Virtual Tour</span>
            </button>
          )}
          
          <button
            onClick={handleFullscreen}
            className="bg-surface/90 backdrop-blur-sm text-text-primary p-2 rounded-md hover:bg-surface transition-all duration-200 shadow-elevation-2"
            aria-label="View fullscreen"
          >
            <Icon name="Maximize" size={18} />
          </button>
        </div>

        {/* Image Counter */}
        <div className="absolute bottom-4 left-4 bg-surface/90 backdrop-blur-sm text-text-primary px-3 py-1 rounded-full text-sm font-medium shadow-elevation-2">
          {selectedImage + 1} / {images?.length || 0}
        </div>
      </div>

      {/* Thumbnail Strip */}
      <div className="p-4">
        <div className="flex space-x-2 overflow-x-auto">
          {images?.map((image, index) => (
            <button
              key={index}
              onClick={() => handleThumbnailClick(index)}
              className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all duration-200 ${
                index === selectedImage 
                  ? 'border-primary shadow-elevation-2' 
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <Image
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
        
        {/* Additional Media Links */}
        <div className="flex flex-wrap gap-3 mt-4">
          {video && (
            <button
              onClick={() => window.open(video, '_blank')}
              className="flex items-center space-x-2 text-accent hover:text-accent-600 text-sm font-medium transition-colors duration-200"
            >
              <Icon name="Play" size={16} />
              <span>Watch Video</span>
            </button>
          )}
          
          <button className="flex items-center space-x-2 text-accent hover:text-accent-600 text-sm font-medium transition-colors duration-200">
            <Icon name="Camera" size={16} />
            <span>360° View</span>
          </button>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {showFullscreen && (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-modal flex items-center justify-center p-4">
          <div className="relative max-w-6xl w-full h-full flex items-center justify-center">
            <button
              onClick={() => setShowFullscreen(false)}
              className="absolute top-4 right-4 bg-surface text-text-primary p-3 rounded-full shadow-elevation-3 hover:bg-secondary-100 transition-all duration-200 z-10"
            >
              <Icon name="X" size={24} />
            </button>
            
            <Image
              src={images[selectedImage]}
              alt={`${title} - Fullscreen`}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WellnessImageGallery;